import assignOffsetPagination from "@/schemas/assignOffsetPagination";
import { WishlistItemEntity } from "@apps/wishlist/entities/WishlistItemEntity";
import { WishlistsBackInStockItemEntity } from "@apps/wishlist/entities/WishlistsBackInStockItemEntity";
import { WishlistsItemsTotalCountEntity } from "@apps/wishlist/entities/WishlistsItemsTotalCountEntity";
import { WishlistWithItemsEntity } from "@apps/wishlist/entities/WishlistWithItemsEntity";
import { WishlistItemSchema } from "@apps/wishlist/schemas/WishlistItemSchema";
import { WishlistsItemsTotalCountSchema } from "@apps/wishlist/schemas/WishlistsItemsTotalCountSchema";
import { wishlistAuthApiClientToken } from "@apps/wishlist/tokens";
import {
    QueryPaginationMeta,
    WISHLIST_ITEMS_SORTING_TYPE,
} from "@apps/wishlist/types";
import { JsonMimeType } from "@lib/ApiClient";
import AuthApiClient from "@lib/ApiClient/AuthApiClient";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { create } from "superstruct";
import { inject, injectable } from "tsyringe";

import { AddApproverResponseSchema } from "./responses/AddApproverResponseSchema";
import { ApproveWishlistResponseSchema } from "./responses/ApproveWishlistResponseSchema";
import { CreateWishlistResponseSchema } from "./responses/CreateWishlistResponseSchema";
import { DeleteApproverResponseSchema } from "./responses/DeleteApproverResponseSchema";
import { DeleteItemResponseSchema } from "./responses/DeleteItemResponseSchema";
import { DeleteWishlistResponseSchema } from "./responses/DeleteWishlistResponseSchema";
import { DuplicateWishlistResponseSchema } from "./responses/DuplicateWishlistResponseSchema";
import { GetBackInStockItemsResponseSchema } from "./responses/GetBackInStockItemsResponseSchema";
import { GetWishlistsResponseSchema } from "./responses/GetWishlistsResponseSchema";
import { MoveItemResponseSchema } from "./responses/MoveItemResponseSchema";
import { ProductStatusesResponseSchema } from "./responses/ProductStatusesResponseSchema";
import { UpdateItemQuantityResponseSchema } from "./responses/UpdateItemQuantityResponseSchema";
import { UpdateItemVariantResponseSchema } from "./responses/UpdateItemVariantResponseSchema";
import { UpdateWishlistResponseSchema } from "./responses/UpdateWishlistResponseSchema";

export type GetWishlistsParams = {
    locale: string;
    orderBy?: WISHLIST_ITEMS_SORTING_TYPE;
    companyId?: number;
};

export type GetItemsTotalCountParams = {
    companyId?: number;
};

export type GetBackInStockWishlistItemsParams = {
    wishlistItems: string;
};

export type DeleteItemParams = {
    productId: number;
    wishlistId?: number;
    variantId?: number;
    companyId?: number;
};

export type DeleteWishlistParams = {
    wishlistId: number;
    companyId?: number;
};

export type MergeWishlistsRequestBody = {
    saveWishlistsToken: string;
};

export type SaveWishlistsRequestBody = {
    email: string;
    isAcceptsEmailMarketing: boolean;
};

export type AddItemRequestBody = {
    wishlistId: number;
    productId: number;
    variantId: number;
};

export type MoveItemRequestBody = {
    wishlistId: number;
    productId: number;
    targetWishlistId: number;
    variantId?: number;
    companyId?: number;
};

export type CreateWishlistRequestBody = {
    title: string;
    companyId?: number;
};

export type DuplicateWishlistRequestBody = {
    wishlistId: number;
};

export type GetWishlistProductStatusesRequestBody = {
    productIds: number[];
    companyId?: number;
};

export type UpdateWishlistItemVariantRequestBody = {
    wishlistId: number;
    productId: number;
    oldVariantId: number;
    newVariantId: number;
};

export type UpdateWishlistItemQuantityRequestBody = {
    wishlistId: number;
    productId: number;
    variantId: number;
    quantity: number;
};

export type CreateDraftOrderRequestBody = {
    wishlistId: number;
};

export type AddApproverRequestBody = {
    wishlistId: number;
    approverId: number;
};

export type DeleteApproverRequestBody = {
    wishlistId: number;
    approverId: number;
};

export type ApproveWishlistRequestBody = {
    wishlistId: number;
};

export type SendNotificationToApproversRequestBody = {
    wishlistId: number;
};

export type UpdateWishlistRequestBody = {
    wishlistId: number;
    newTitle?: string;
    isDefault?: boolean;
    isPrivate?: boolean;
    companyId?: number;
};

@injectable()
export class WishlistsApiService {
    public constructor(
        @inject(wishlistAuthApiClientToken)
        private readonly apiClient: AuthApiClient
    ) {}

    getWishlists(
        queryParams: GetWishlistsParams
    ): Observable<WishlistWithItemsEntity[]> {
        return this.apiClient
            .get({
                url: "/getWishlists",
                responseType: "json",
                queryParams,
            })
            .pipe(
                map((response) => {
                    return create(response.body, GetWishlistsResponseSchema);
                }),
                map((result) => {
                    return result.wishlistsWithItems;
                })
            );
    }

    getWishlistItems(queryParams: QueryPaginationMeta) {
        return this.apiClient
            .get({
                url: "/getItems",
                queryParams,
            })
            .pipe(
                map((response) => {
                    return create(
                        response.body,
                        assignOffsetPagination(WishlistItemSchema)
                    );
                })
            );
    }

    getItemsTotalCount(
        queryParams: GetItemsTotalCountParams
    ): Observable<WishlistsItemsTotalCountEntity> {
        return this.apiClient
            .get({
                url: "/getItemsTotalCount",
                responseType: "json",
                queryParams,
            })
            .pipe(
                map((response) => {
                    return create(
                        response.body,
                        WishlistsItemsTotalCountSchema
                    );
                })
            );
    }

    mergeWishlists(reqBody: MergeWishlistsRequestBody): Observable<unknown> {
        return this.apiClient
            .post({
                url: "/attachSavedWishlists",
                body: JSON.stringify(reqBody),
                contentType: JsonMimeType,
                responseType: "text",
            })
            .pipe(
                map((result) => {
                    return result;
                })
            );
    }

    saveWishlists(reqBody: SaveWishlistsRequestBody): Observable<unknown> {
        return this.apiClient
            .post({
                url: "/saveWishlistItems",
                body: JSON.stringify(reqBody),
                contentType: JsonMimeType,
                responseType: "text",
            })
            .pipe(
                map((result) => {
                    return result;
                })
            );
    }

    addItem(reqBody: AddItemRequestBody): Observable<WishlistItemEntity> {
        return this.apiClient
            .post({
                url: "/addItem",
                body: JSON.stringify(reqBody),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    return create(response.body, WishlistItemSchema);
                }),
                map((result) => {
                    return result;
                })
            );
    }

    public deleteItem(
        queryParams: DeleteItemParams
    ): Observable<WishlistItemEntity> {
        return this.apiClient
            .delete({
                url: "/deleteItem",
                queryParams,
            })
            .pipe(
                map((response) => {
                    return create(response.body, DeleteItemResponseSchema);
                }),
                map((result) => {
                    return result.deletedItem;
                })
            );
    }

    public moveItem(
        reqBody: MoveItemRequestBody
    ): Observable<WishlistItemEntity> {
        return this.apiClient
            .post({
                url: "/moveItem",
                body: JSON.stringify(reqBody),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    return create(response.body, MoveItemResponseSchema);
                }),
                map((result) => {
                    return result.movedItem;
                })
            );
    }

    public updateWishlist(reqBody: UpdateWishlistRequestBody) {
        return this.apiClient
            .post({
                url: "/updateWishlist",
                body: JSON.stringify(reqBody),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    return create(response.body, UpdateWishlistResponseSchema);
                }),
                map((result) => {
                    return result.updatedWishlist;
                })
            );
    }

    public createWishlist(reqBody: CreateWishlistRequestBody) {
        return this.apiClient
            .post({
                url: "/createWishlist",
                body: JSON.stringify(reqBody),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    return create(response.body, CreateWishlistResponseSchema);
                }),
                map((result) => {
                    return result.createdWishlist;
                })
            );
    }

    public deleteWishlist(queryParams: DeleteWishlistParams) {
        return this.apiClient
            .delete({
                url: "/deleteWishlist",
                queryParams,
            })
            .pipe(
                map((response) => {
                    return create(response.body, DeleteWishlistResponseSchema);
                }),
                map((result) => {
                    return result.deletedWishlist;
                })
            );
    }

    public duplicateWishlist(reqBody: DuplicateWishlistRequestBody) {
        return this.apiClient
            .post({
                url: "/duplicateWishlist",
                body: JSON.stringify(reqBody),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    return create(
                        response.body,
                        DuplicateWishlistResponseSchema
                    );
                }),
                map((result) => {
                    return result.wishlistsWithItems;
                })
            );
    }

    public getWishlistProductStatuses(
        reqBody: GetWishlistProductStatusesRequestBody
    ) {
        return this.apiClient
            .post({
                url: "/getWishlistProductStatuses",
                body: JSON.stringify(reqBody),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    return create(response.body, ProductStatusesResponseSchema);
                }),
                map((result) => {
                    return result.results;
                })
            );
    }

    public getBackInStockWishlistItems(
        queryParams: GetBackInStockWishlistItemsParams
    ): Observable<WishlistsBackInStockItemEntity[]> {
        return this.apiClient
            .get({
                url: "/getBackInStockWishlistItems",
                queryParams,
                responseType: "json",
            })
            .pipe(
                map((response) => {
                    return create(
                        response.body,
                        GetBackInStockItemsResponseSchema
                    );
                }),
                map((response) => {
                    return response.results;
                })
            );
    }

    public updateWishlistItemVariant(
        reqBody: UpdateWishlistItemVariantRequestBody
    ) {
        return this.apiClient
            .put({
                url: "/updateWishlistItemVariant",
                body: JSON.stringify(reqBody),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    return create(
                        response.body,
                        UpdateItemVariantResponseSchema
                    );
                }),
                map((result) => {
                    return result;
                })
            );
    }

    public updateWishlistItemQuantity(
        reqBody: UpdateWishlistItemQuantityRequestBody
    ) {
        return this.apiClient
            .put({
                url: "/updateWishlistItemQuantity",
                body: JSON.stringify(reqBody),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    return create(
                        response.body,
                        UpdateItemQuantityResponseSchema
                    );
                }),
                map((result) => {
                    return result;
                })
            );
    }

    public createDraftOrder(reqBody: CreateDraftOrderRequestBody) {
        return this.apiClient
            .post({
                url: "/createDraftOrder",
                body: JSON.stringify(reqBody),
                contentType: JsonMimeType,
                responseType: "text",
            })
            .pipe(
                map((response) => {
                    return response;
                })
            );
    }

    public addApprover(reqBody: AddApproverRequestBody) {
        return this.apiClient
            .post({
                url: "/addApprover",
                body: JSON.stringify(reqBody),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    return create(response.body, AddApproverResponseSchema);
                }),
                map((result) => {
                    return result;
                })
            );
    }

    public deleteApprover(reqBody: DeleteApproverRequestBody) {
        return this.apiClient
            .post({
                url: "/deleteApprover",
                body: JSON.stringify(reqBody),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    return create(response.body, DeleteApproverResponseSchema);
                }),
                map((result) => {
                    return result;
                })
            );
    }

    public approveWishlist(reqBody: ApproveWishlistRequestBody) {
        return this.apiClient
            .post({
                url: "/approveWishlist",
                body: JSON.stringify(reqBody),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    return create(response.body, ApproveWishlistResponseSchema);
                }),
                map((response) => {
                    return response;
                })
            );
    }

    public sendNotificationToApprovers(
        reqBody: SendNotificationToApproversRequestBody
    ) {
        return this.apiClient
            .post({
                url: "/sendNotificationToApprovers",
                body: JSON.stringify(reqBody),
                contentType: JsonMimeType,
                responseType: "text",
            })
            .pipe(
                map((response) => {
                    return response;
                })
            );
    }
}
